import { J_PER_M2_PER_CAL_PER_CM2 } from 'models';

export const J_PER_CAL = 4.184;
export const JPM2_PER_CALPCM2 = J_PER_M2_PER_CAL_PER_CM2;
export const M2_PER_CM2 = 1e-4;

export const PPE_CATEGORY_COLORS: Record<number, string> = {
    0: 'green',
    1: 'blue',
    2: 'yellow',
    3: 'orange',
    4: 'red',
};

export const PPE_CATEGORY_LABELS: Record<number, string> = {
    0: 'No PPE Required',
    1: 'Category 1',
    2: 'Category 2',
    3: 'Category 3',
    4: 'Category 4',
};

export const PPE_CATEGORY_SHORT_LABELS: Record<number, string> = {
    0: 'No PPE',
    1: 'Cat 1',
    2: 'Cat 2',
    3: 'Cat 3',
    4: 'Cat 4',
};

export type EnergyUnit = 'J' | 'kJ' | 'cal' | 'kcal';
export type IncidentEnergyUnit = 'J/m²' | 'cal/cm²';
export type AreaUnit = 'm²' | 'cm²' | 'mm²';

const ENERGY_FACTOR_TO_J: Record<EnergyUnit, number> = {
    J: 1,
    kJ: 1_000,
    cal: J_PER_CAL,
    kcal: 1_000 * J_PER_CAL,
};

const INCIDENT_ENERGY_FACTOR_TO_JPM2: Record<IncidentEnergyUnit, number> = {
    'J/m²': 1,
    'cal/cm²': JPM2_PER_CALPCM2,
};

const AREA_FACTOR_TO_M2: Record<AreaUnit, number> = {
    'm²': 1,
    'cm²': M2_PER_CM2,
    'mm²': 1e-6,
};

export const convertEnergy = (value: number, from: EnergyUnit, to: EnergyUnit): number => {
    if (from === to) return value;
    return (value * ENERGY_FACTOR_TO_J[from]) / ENERGY_FACTOR_TO_J[to];
};

export const convertIncidentEnergy = (value: number, from: IncidentEnergyUnit, to: IncidentEnergyUnit): number => {
    if (from === to) return value;
    return (value * INCIDENT_ENERGY_FACTOR_TO_JPM2[from]) / INCIDENT_ENERGY_FACTOR_TO_JPM2[to];
};

export const convertArea = (value: number, from: AreaUnit, to: AreaUnit): number => {
    if (from === to) return value;
    return (value * AREA_FACTOR_TO_M2[from]) / AREA_FACTOR_TO_M2[to];
};

export const pickEArcDisplayUnit = (valuesJ: number[]): { data: number[]; unit: 'J' | 'kJ' } => {
    const maxAbs = valuesJ.reduce((acc, v) => Math.max(acc, Math.abs(v)), 0);
    if (maxAbs >= 1_000) {
        return { data: valuesJ.map((v) => convertEnergy(v, 'J', 'kJ')), unit: 'kJ' };
    }
    return { data: valuesJ.slice(), unit: 'J' };
};

export type WilkinsInputs = {
    enclosure_factor?: number;
    working_distance?: { value: number };
    wilkins_a?: number;
    wilkins_x?: number;
};

const INCIDENT_ENERGY_EXPLANATION_PREFIX =
    'Thermal energy per unit area at the working distance, taken as the final value of the integrated incident-energy curve over the arc window.';

export const formatIncidentEnergyExplanation = (arcFlash: WilkinsInputs): string => {
    const k = arcFlash.enclosure_factor ?? 'k';
    const d = arcFlash.working_distance?.value ?? 'd';
    const a = arcFlash.wilkins_a ?? 'a';
    const x = arcFlash.wilkins_x ?? 'x';
    return `${INCIDENT_ENERGY_EXPLANATION_PREFIX} Formula: E_i = ${k} · E_arc / (${a}² + ${d}^${x}).`;
};
